import type { CourseQuizContent } from '../types'

export const negotiateToWinContent: CourseQuizContent = {
  courseSlug: 'negotiate-to-win-essentials-for-leaders',
  courseName: 'Negotiate to Win',

  goalQuestion: {
    title: 'How do you feel about negotiations today?',
    options: [
      {
        label: 'I avoid them when I can. Conflict isn’t me.',
        value: 'avoid-it',
      },
      {
        label: 'I hold my own, but I’m mostly winging it.',
        value: 'winging-it',
      },
      {
        label: 'I negotiate all the time and I’m here for an edge.',
        value: 'want-an-edge',
      },
    ],
  },

  levelQuestion: {
    title: 'Is there anything you’re looking to negotiate right now?',
    options: [
      {
        label: 'My own value: salary, promotion, resources, recognition.',
        value: 'own-value',
      },
      {
        label: 'Closing deals: clients, vendors, partners, rates.',
        value: 'closing-deals',
      },
      {
        label: 'Hard conversations: conflict, tension, or getting aligned.',
        value: 'hard-conversations',
      },
      {
        label: 'Influence: buy-in from people who don’t report to me.',
        value: 'buy-in',
      },
    ],
  },

  primaryQuestionSlot: 'level',

  interstitial1: {
    pill: 'IN THIS COURSE',
    headline:
      'Go beyond just learning about negotiations. Run one, and get scored on it.',
    body: 'Practice with micro-negotiations and open-question drills, run a live AI roleplay with targeted feedback, and finish with your own Negotiation Playbook.',
    image: 'https://static.masterclass.com/nego-interstitial-1.png',
    imageAlt: 'The Negotiation Playbook course materials',
  },

  interstitial2: {
    pill: 'DID YOU KNOW',
    headline: 'You don’t need to be a negotiation shark to win.',
    body: 'Alexandra Carter teaches empathy-driven negotiation to U.N. diplomats, and it’s the mindset behind Rich Paul’s $5B in deals. You don’t have to be aggressive to get more. You have to be prepared.',
    image: 'https://static.masterclass.com/nego-interstitial-2.png',
    imageAlt: 'Two people talking across a table',
  },

  profiles: [
    {
      key: 'own-value',
      headline:
        'You want to negotiate your value and get what your work deserves',
      lead: 'You negotiate hard for everyone except yourself. Asking for your work’s worth isn’t arrogance. It is a skill that this course helps you build.',
      bullets: [
        {
          text: '**Build a brag bank and Past Success Statement**, so you have the evidence ready before you ask.',
        },
        {
          text: 'Use **power priming** to walk in sounding confident instead of apologetic.',
        },
        {
          text: 'Start with **the Mirror**: win the negotiation with yourself before the one across the table.',
        },
      ],
    },
    {
      key: 'closing-deals',
      headline: 'You want to close better deals, with the relationships intact',
      lead: 'When you close deals on instinct, you leave value on the table. This course replaces winging it with a system: needs audits, trade-off maps, and closes that hold up even after the handshake.',
      bullets: [
        {
          text: 'Run a **Needs Audit** before every deal, covering non-negotiables, flexible zones, and your true bottom line.',
        },
        {
          text: '**Chart needs on both sides** to find the invisible engine of the deal and the trade-offs that unlock it.',
        },
        {
          text: 'Close against a **good-agreement checklist**, so “done” also means durable.',
        },
      ],
    },
    {
      key: 'hard-conversations',
      headline:
        'You need to negotiate your way through hard conversations in a way that strengthens connections',
      lead: 'No one likes conflict. Without having a structure you can rely on to deal with conflict, you find yourself avoiding it. This course gives you the structure you need to tackle conflict head-on.',
      bullets: [
        {
          text: '**Define the problem beneath the surface**, so you stop negotiating symptoms.',
        },
        {
          text: 'Read the two emotions that derail high-stakes conversations, **fear and guilt**, and learn to navigate both.',
        },
        {
          text: 'Map **Emotional Currents and Riptides** before the conversation, so tension doesn’t catch you unprepared.',
        },
      ],
    },
    {
      key: 'buy-in',
      headline:
        'You want to negotiate influence and secure buy-in from people you don’t directly manage',
      lead: 'Success in your role largely depends on people who don’t report to you. This course turns buy-in into a system, so influence stops depending on goodwill.',
      bullets: [
        {
          text: 'Master **strategic open questions**, the “fishing with a net” approach, to surface what stakeholders actually need.',
        },
        {
          text: 'Build momentum through **small tracked agreements** instead of waiting for the grand yes.',
        },
        {
          text: 'Leave with your **Negotiation Playbook**, a repeatable system for building buy-in at work.',
        },
      ],
    },
  ],

  paceNotes: [
    { key: 'lt1h', weeks: '10 to 12 weeks' },
    { key: '1to3h', weeks: '4 to 6 weeks' },
    { key: '3to5h', weeks: '2 to 3 weeks' },
    { key: '5plush', weeks: '2 weeks' },
  ],

  previewLessons: [
    {
      title: 'Build the Relationship First: Why the Contract Isn’t the Deal',
      mediaUuid: 'cdda5e60-7151-47a7-9e7d-5fb494a79069',
      image: 'https://static.masterclass.com/nego-video-1.png',
    },
    {
      title: 'Surface the Other Side’s Needs',
      mediaUuid: 'eea19613-0c8a-44f6-85c0-b69064b6b86b',
      image: 'https://static.masterclass.com/nego-video-2.png',
    },
  ],

  ctaHighlights: [
    'Taught by world-renowned negotiation experts.',
    'Create your personal Negotiation Playbook.',
    'Earn a shareable MasterClass Certificate.',
  ],
}
